import { ApiProblem, Deadline } from '@cdn/api';
import type { Identity, SignInRequest } from '../port';
import type { Vault } from '../vault';

interface Specs {
	/** Origin of the Workspace central administration API, or a same-origin proxy path */
	authority: string;
	vault: Vault;
	fetch?: typeof fetch;
	/** Milliseconds a request may take (`Deadline.DEFAULT` otherwise) */
	timeout?: number;
}

/**
 * The **simulated** email sign-in of the development mock (`npm run mock`) and of the tests: `POST
 * /v1/local/sessions` answers an opaque bearer token, and `POST /v1/local/users` registers an account of the
 * double. It is selected only by naming it (`VITE_IDENTITY_ADAPTER=administration`) and is never a fallback:
 * Beyond Accounts is the only identity of a person, in development too. Signing out forgets the token here.
 */
export class AdministrationIdentity implements Identity {
	readonly name = 'administration';
	readonly validated = true;
	readonly methods = ['local'] as const;

	#authority: string;
	#vault: Vault;
	#fetch: typeof fetch;
	#timeout: number;

	constructor({ authority, vault, fetch: custom, timeout }: Specs) {
		this.#authority = authority;
		this.#vault = vault;
		this.#fetch = custom ?? ((input, init) => globalThis.fetch(input, init));
		this.#timeout = timeout ?? Deadline.DEFAULT;
	}

	async restore() {
		return this.#vault.read();
	}

	async signin(request: SignInRequest) {
		if (request.method !== 'local') throw new ApiProblem('UNSUPPORTED_INPUT', `The administration adapter has no "${request.method}" sign-in`, 0);

		request.name && (await this.#post('/v1/local/users', { name: request.name, email: request.email }));
		const { token } = (await this.#post('/v1/local/sessions', { email: request.email })) as { token?: string };
		if (!token) throw new ApiProblem('INTERNAL', 'The administration answered without a token', 0);
		this.#vault.write(token);
		return token;
	}

	async signout() {
		this.#vault.clear();
	}

	async #post(path: string, body: object): Promise<unknown> {
		const exchange = async (signal: AbortSignal) => {
			const init: RequestInit = { method: 'POST', headers: { 'content-type': 'application/json', accept: 'application/json' }, body: JSON.stringify(body), credentials: 'omit', signal };
			const answer = await this.#fetch(`${this.#authority}${path}`, init);
			const document = await answer.json().catch(() => undefined);
			if (!answer.ok) throw ApiProblem.from(answer.status, document);
			return document;
		};
		try {
			return await new Deadline(this.#timeout).run(exchange);
		} catch (thrown) {
			if (thrown instanceof ApiProblem) throw thrown;
			throw new ApiProblem('UNAVAILABLE', 'The administration service did not answer', 0);
		}
	}
}
